import { action, observable } from 'mobx';
import { ImportPatientEditForm } from 'application/import/forms';
import {BaseEditViewStore, BaasicDropdownStore} from 'core/stores';
import {applicationContext} from 'core/utils';
import {ImportDetailService, ImportService} from 'application/import/services';
import {ProductService} from 'application/product/services';
import {PatientService} from 'application/patient/services';
import { LocalPatientService } from "application/patient/services";
import { TrackingCaseService, CaseStatusService } from "application/tracking-case/services";
import _ from 'lodash';
import { ModalParams } from 'core/models';

@applicationContext
class ImportPatientEditViewStore extends BaseEditViewStore {
    @observable accessor itemsConstructed = false;
    @observable accessor isFormDisabled = false;
    @observable accessor isProcessing = false;   
    @observable accessor facility = null;  
    @observable accessor primaryInsurance = null;   
    @observable accessor reviewed = false;
    @observable accessor isAvalibleForImport = true;
    @observable accessor trackingCaseList = []; 
    @observable accessor patient = null; 
    @observable accessor nextPatientGroupId = null;
    @observable accessor previousPatientGroupId = null;
    @observable accessor isNewNextPatient = false;
    @observable accessor isNewPreviousPatient = false;

    constructor(rootStore) {
        const importHeaderId = rootStore.routerStore.routerState.params.id;
        const id = rootStore.routerStore.routerState.params.importPatientId;
        const service =  new ImportDetailService(rootStore.application.baasic.apiClient);

        super(rootStore, {
            name: 'import patient',
            id: id,
            Form: ImportPatientEditForm,
            actions: () => {
                return {
                    get: async (id) => {
                        const response = await service.getByPatient({ importHeaderId: importHeaderId, patientGroupId: id , embed: 'ImportDetailStatus,ImportDetailType,ItemCategory,ItemCategory_' });
                        this.items = response.item;
                        let patient = _.first(response.item);
                        let items = _.sortBy(response.item, ['templateId'], ['asc']);

                        this.isAvalibleForImport = !response.item.every(v => v.errorMessage && v.errorMessage.includes("Reference not valid") === true);

                        let groupedItems = _.groupBy(items, item => {
                            return item.itemGroupId;
                        });
                        await this.initializePatientNavigation(importHeaderId, id);
                        return {
                            firstName: patient.firstName,
                            lastName: patient.lastName,
                            patientId: patient.patientId,
                            patientBtId: patient.patientBtId,
                            address1: patient.address1,
                            address2: patient.address2,
                            city: patient.city,
                            state: patient.state,
                            dateOfBirth: patient.patientDetail ? patient.patientDetail.data.dateOfBirth : '',
                            phone: patient.phone,                            
                            items : groupedItems
                        };
                    },
                    update: async (resource) => {
                        let items = [];
                        _.each(this.item.items, (groupItems) => {
                                let firstItem = _.first(groupItems);

                                let category = firstItem.itemCategory_;
                                let categoryExist = category || firstItem.itemCategory;       
                                let billableToMedicareField = categoryExist ? 
                                    resource.templates['template-' + firstItem.itemGroupId] : null;
                                let categoryItem = _.map(groupItems, item => {
                                    let product = resource.items['item-' + item.id];
                                    return _.extend(item, {
                                        itemId: product.product.itemId,
                                        quantity: product.quantity,
                                        salesOrderQuantity: product.salesOrderQuantity,
                                        billableToMedicare: billableToMedicareField ? billableToMedicareField.billableToMedicare : false,
                                        reviewed: this.reviewed
                                    });
                                });
                                items = _.concat(items, categoryItem);
                        });
                        await service.batchUpdate(items);
                    }
                }
            },
            FormClass: ImportPatientEditForm
        });

        this.brightreeBaseUrl = this.rootStore.applicationStore.app.baasic.settings.brightreeBaseUrl;
        this.itemGroupEditModal = new ModalParams({onClose: async () => { 
            this.loaderStore.suspend();        
            await this.service.process({ importHeaderId: this.importHeaderId, patientKey: this.item.patientId }); 
            await this.reinitializeData();
            this.loaderStore.resume();        
        }});

        this.productService = new ProductService(this.rootStore.application.baasic.apiClient);
        this.service = service;
        this.importService = new ImportService(rootStore.application.baasic.apiClient);
        this.patientService = new PatientService(rootStore.application.baasic.apiClient);
        this.importHeaderId = importHeaderId;
        this.id = id;
        this.products = [];

        this.trackingCaseService = new TrackingCaseService(this.rootStore.application.baasic.apiClient);
        this.caseStatusService = new CaseStatusService(this.rootStore.application.baasic.apiClient);
        this.localPatientService = new LocalPatientService(rootStore.application.baasic.apiClient);

        this.getIsProcessing();
        this.getFacility();
    }

    @action.bound
    async goToPreview(patientBtId, firstName, lastName) {
        this.rootStore.routerStore.setTransitionData({
            navigationTitle: firstName + ' ' + lastName
        });
        this.rootStore.routerStore.goTo(
            'master.app.main.patient.preview.general',
            { id: patientBtId, firstName: firstName, lastName: lastName }
        );
    }

    @action.bound
    async reinitializeData() {
        this.itemsConstructed = false;
        this.item = null;
        this.itemInitialized = false;
        
        await this.getImportDataAndSetProductsStore(this.id);
        await this.getIsProcessing();
        await this.getFacility();
    }

    @action
    setItem(item) {
        this.item = item;
        this.itemInitialized = true;

        this.setTrackingCaseList(item);
        this.getPatient(item); 
    }

    @action.bound
    async getPatient(item) {
        this.patient = await this.localPatientService.find({ patientId: item.patientId });
        this.isCreateCase = this.patient.data.item && this.patient.data.item.length > 0;
    }

    @action.bound
    async createCase(item) {
        const patientBtId = item[0].patientBtId;
        this.btPatient = await this.patientService.get(patientBtId);
        
        // const productIds = item.map(i=> {
        //     let selectedProducts = this.products.find(p=>p.itemId == i.itemId);
        //     return selectedProducts && {productId:selectedProducts.id};
        // }).filter(Boolean);

        const productResponse = await this.productService.find({
            itemIds: item.map(i => i.itemId).join(), 
            isActive: true,
            rpp: 20 
        });

        const productIds = productResponse.data.item.map(i => {
            return {
                productId: i.id
            };
        });

        let payorSource = this.btPatient.data.patientInsuranceInfo.payors.find(i=>i.payorLevel === "Primary");
        if(payorSource && payorSource.payorKey === 0){
            payorSource = this.btPatient.data.patientInsuranceInfo.payors.find(i=>i.payorLevel === "Secondary");
        }
        
        try{
            let response = await this.trackingCaseService.create({
                itemCategoryId: item[0].itemCategory_.id,
                facilityId: this.facility.id,
                patientId: this.patient.data.item[0].id,
                reference: item.map(i=> i.reference).join(", "),
                validFrom: new Date(),
                trackingCaseProducts: productIds,
                payorSource: payorSource ? payorSource.payorKey : 0 
            });
            this.rootStore.notificationStore.success("Created Case successfully.");
            window.open(this.rootStore.routerStore.findRoute('master.app.main.tracking-case.preview').pattern.replace(":id",response.data.id));
                           
        }catch(err){
            this.rootStore.notificationStore.error("Error occurred while trying to create a case.");  
        }
    }

    @action.bound
    async setTrackingCaseList(item) {
        const caseStatusList = await this.caseStatusService.lookup({ searchQuery: "active" });

        let response = await this.trackingCaseService.find(
            {
                patientPatientId:item.patientId, 
                caseStatusId: caseStatusList.item[0].id,
                embed:"itemCategory"
            }
        );
        this.trackingCaseList = response.data.item;
    }

    @action.bound
    async toggleReviewed() {
        try{
            var resource = {
                importHeaderId: this.importHeaderId,
                patientGroupId: this.id,
                reviewed: !this.reviewed
            };
            this.reviewed = !this.reviewed;               
            await this.service.updateReviewed(resource); 
            this.rootStore.notificationStore.success("Patient reviewed successfully updated.");                                       
        }catch(err){
            this.rootStore.notificationStore.error("Error occured while trying to update patient review.");                                                       
        }
    }

    @action.bound
    async initializePatientNavigation(importHeaderId, patientGroupId) {
        const response = await this.service.getPatientNavigation(importHeaderId, patientGroupId);
        this.previousPatientGroupId = response.data.previousPatientGroupId;
        this.nextPatientGroupId = response.data.nextPatientGroupId;
        this.isNewNextPatient = response.data.isNewNextPatient;
        this.isNewPreviousPatient = response.data.isNewPreviousPatient;
    }

    @action.bound
    async getPrimaryInsurance(patientId) {
        if(patientId){
            const response = await this.patientService.getPatientPrimaryInsurance(patientId);
            this.primaryInsurance  = response;
        }
    }

    @action.bound
    async getFacility() {
        const response = await this.importService.get(this.importHeaderId, {embed: 'Facility'});
        if(response.data && response.data.facility){
            this.facility = response.data.facility;
        }
    }

    @action.bound
    async getIsProcessing() {
        let response = await this.service.isRunning({importHeaderId: this.importHeaderId});
        this.isProcessing = response;
    }

    @action.bound
    async attachProductStore(item, disabled) {
        let id = item.id;
        item.productStore = new BaasicDropdownStore({
            filterable: true,
            dataItemKey: 'itemId',
            textField: 'itemId',
            disabled: disabled,
            returnObject: true
        }, {
            fetchFunc: async (term) => {
                if(!term)
                    return this.products;

                let productResponse = await this.productService.find({
                    searchQuery: term,
                    isActive: true,
                    rpp: 20 
                });

                this.products = productResponse.data.item;

                // let filteredProducts = _.filter(this.products, p => {
                //     return p.itemId.toLowerCase().startsWith(term.toLowerCase());
                // });

                // return filteredProducts;

                return productResponse.data.item;

            },
            onChange: (product) => {
                this.setProduct(product, id);
                item.itemId = product.itemId;
                item.itemName = product.description;
                item.procedureCode = product.procedureCode;
            }
        });
    }

    @action.bound
    setProduct(item, importDetailId) {
        this.form.fields.get('items').fields.get('item-'+importDetailId).fields.get('product').set(item);      
    }

    @action.bound
    async getImportDataAndSetProductsStore(id) {
        await super.getResource(id);
        this.reviewed = _.every(this.items, ['reviewed', true]);        
        await this.getPrimaryInsurance(this.item.patientBtId);

        let productResponse = await this.productService.find({
            itemIds: this.items.map(i => i.itemId).join(), 
            isActive: true,
            rpp: 20 
        });

        this.products = productResponse.data.item;

        let formFieldsDisabledValues = [];
        let itemsField = this.form.fields.get('items');
        let templatesField = this.form.fields.get('templates');
        _.each(this.item.items, (groupItems) => {
                let firstItem = _.first(groupItems);                
                let category = firstItem.itemCategory_;
                let categoryExist = category || firstItem.itemCategory;                
                if(categoryExist){
                    let checkboxDisabled = _.every(groupItems, item => {
                        return item.importDetailStatus && item.importDetailStatus.abrv === 'imported';
                    });
                    let field = {
                        name: 'template-' + firstItem.itemGroupId,
                        fields: [{
                            name: 'billableToMedicare',
                            type: 'bool',
                            disabled: checkboxDisabled
                        }]
                    };
                    field.fields[0].value = firstItem.billableToMedicare;
                    templatesField.add(field);
                }
                for( const item of groupItems){
                    let fieldDisabled = item.importDetailStatus && item.importDetailStatus.abrv === 'imported';
                    formFieldsDisabledValues.push(fieldDisabled);

                    let field = {
                    name: 'item-' + item.id,
                    fields: [{
                        name: 'product',
                        label: 'Product',
                        placeholder: 'Select Product'
                    },{
                        name: 'quantity',
                        placeholder: 'Enter Quantity',
                        rules: 'integer',
                        disabled: fieldDisabled
                    },{
                        name: 'salesOrderQuantity',
                        placeholder: 'Enter SO Quantity',
                        rules: 'integer',
                        disabled: fieldDisabled
                    }]
                    };
                    field.fields[1].value = item.quantity;
                    field.fields[2].value = item.salesOrderQuantity;
                    itemsField.add(field);

                    let product = _.find(this.products, p => {
                        return p.itemId.toLowerCase() == item.itemId.toLowerCase();
                    });
                    if(product){
                        item.itemId = product.itemId;
                        item.itemName = product.description;
                        item.procedureCode = product.procedureCode;
                    }

                    if(item.itemId)
                    {
                        this.setProduct(item.itemId, item.id);
                    }

                    this.attachProductStore(item, fieldDisabled);
                }
        });

        this.isFormDisabled = _.every(formFieldsDisabledValues, item => { return item; });        

        this.itemsConstructed = true;
    }

    @action.bound
    async goToNextPatient() {
        if (!this.nextPatientGroupId || this.nextPatientGroupId == null)
            return;
        
        if(this.isNewNextPatient)
        {
            await this.rootStore.routerStore.goTo(
                'master.app.main.patient-detail.edit', {
                    id: this.rootStore.routerStore.routerState.params.id,
                    importPatientId: this.nextPatientGroupId
                }
            );
        }
        else
        {
            await this.rootStore.routerStore.goTo(
                'master.app.main.import.detail.edit', {
                    id: this.rootStore.routerStore.routerState.params.id,
                    importPatientId: this.nextPatientGroupId
                }
            );
        }
        window.location.reload();
    }

    @action.bound
    async goToPreviousPatient() {  
        if (!this.previousPatientGroupId || this.previousPatientGroupId == null)
            return;
    
        if(this.isNewPreviousPatient)
        {
            await this.rootStore.routerStore.goTo(
                'master.app.main.patient-detail.edit', {
                    id: this.rootStore.routerStore.routerState.params.id,
                    importPatientId: this.previousPatientGroupId
                }
            );
        }
        else
        {
            await this.rootStore.routerStore.goTo(
                'master.app.main.import.detail.edit', {
                    id: this.rootStore.routerStore.routerState.params.id,
                    importPatientId: this.previousPatientGroupId
                }
            );
        }

        window.location.reload();
    }

    @action.bound
    async getResource(id) {
        await super.getResource(id);
        this.reviewed = _.every(this.items, ['reviewed', true]);    
        await this.getPrimaryInsurance(this.item.patientBtId);

        let productResponse = await this.productService.find({
            itemIds: this.items.map(i => i.itemId).join(), 
            isActive: true,
            rpp: 20 
        });
        
        this.products = productResponse.data.item;

        let formFieldsDisabledValues = [];
        let itemsField = this.form.fields.get('items');
        let templatesField = this.form.fields.get('templates');
        _.each(this.item.items, (groupItems) => {
                let firstItem = _.first(groupItems);                
                let category = firstItem.itemCategory_;
                let categoryExist = category || firstItem.itemCategory;                
                if(categoryExist){
                    let checkboxDisabled = _.every(groupItems, item => {
                        return item.importDetailStatus && item.importDetailStatus.abrv === 'imported';
                    });
                    let field = {
                        name: 'template-' + firstItem.itemGroupId,
                        fields: [{
                            name: 'billableToMedicare',
                            type: 'bool',
                            disabled: checkboxDisabled
                        }]
                    };
                    field.fields[0].value = firstItem.billableToMedicare;
                    templatesField.add(field);
                }
                for( const item of groupItems){
                    let fieldDisabled = item.importDetailStatus && item.importDetailStatus.abrv === 'imported';
                    formFieldsDisabledValues.push(fieldDisabled);

                    let field = {
                    name: 'item-' + item.id,
                    fields: [{
                        name: 'product',
                        label: 'Product',
                        placeholder: 'Select Product'
                    },{
                        name: 'quantity',
                        placeholder: 'Enter Quantity',
                        rules: 'integer',
                        disabled: fieldDisabled
                    },{
                        name: 'salesOrderQuantity',
                        placeholder: 'Enter SO Quantity',
                        rules: 'integer',
                        disabled: fieldDisabled
                    }]
                    };
                    field.fields[1].value = item.quantity;
                    field.fields[2].value = item.salesOrderQuantity;
                    itemsField.add(field);

                    let product = _.find(this.products, p => {
                        return p.itemId.toLowerCase() == item.itemId.toLowerCase();
                    });
                    if(product){
                        item.itemId = product.itemId;
                        item.itemName = product.description;
                        item.procedureCode = product.procedureCode;
                    }

                    if(item.itemId)
                    {
                        this.setProduct(item.itemId, item.id);
                    }

                    this.attachProductStore(item, fieldDisabled);
                }
        });

        this.isFormDisabled = _.every(formFieldsDisabledValues, item => { return item; });        

        this.itemsConstructed = true;
    }

    @action.bound
    async importItems() {
        this.loaderStore.suspend();        
        this.form.setFieldsDisabled(true);
        try {
            await this.service.import({ importHeaderId: this.importHeaderId, patientKey: this.item.patientId });     
            await this.reinitializeData();         
            this.rootStore.notificationStore.success("Import successfully done.");                       
        }
        catch(err) {
            this.rootStore.notificationStore.error("Error occured on import.");
        }

        this.form.setFieldsDisabled(false);    
        this.loaderStore.resume();        
    }

    @action.bound
    async updateResource(resource) {
        this.loaderStore.suspend();
        this.form.setFieldsDisabled(true);
        try {
            await this.actions.update({
                id: this.id,
                ...resource,
            });
            await this.service.process({ importHeaderId: this.importHeaderId, patientKey: this.item.patientId }); 
            await this.reinitializeData();
            this.rootStore.notificationStore.success("Changes successfully saved.");
        }
        catch(err) {
            this.rootStore.notificationStore.error("Error occured while saving.");
        }
        this.form.setFieldsDisabled(false);
        this.loaderStore.resume();

    }

    @action.bound
    openItemGroupEdit(itemGroupId, patientKey) {
        if(itemGroupId){
            this.itemGroupEditModal.open({patientKey: patientKey, itemGroupId: itemGroupId});
        }
    }

    @action.bound
    async onInit({ initialLoad }) {
        if(!initialLoad) {
            this.rootStore.routerStore.goBack();
        }
    }
}

export default ImportPatientEditViewStore;
